import { useState, useEffect } from "react";
import { API } from "@/lib/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  UserPlus,
  Trash2,
  Shield,
  ShieldOff,
  Copy,
  Check,
  Mail,
  RefreshCw,
  AlertTriangle,
  MailX,
  MailCheck,
  Key,
  Eye,
  UserCog,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import UserRolesPopover, { summariseUserRoles } from "@/components/UserRolesPopover";

// Role descriptions
const ROLE_INFO = {
  admin: { label: "Admin", description: "Full access - manage users, reset data, all features", color: "bg-red-100 text-red-700" },
  user: { label: "User", description: "Can create/edit clients, send emails, use all features", color: "bg-blue-100 text-blue-700" },
  viewer: { label: "Viewer", description: "Read-only access - can view but not edit data", color: "bg-slate-100 text-slate-700" },
};

function SalesRoleInput({ userId, currentRole }) {
  const [role, setRole] = useState(currentRole);
  const [saving, setSaving] = useState(false);

  const save = async (newRole) => {
    setRole(newRole);
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/users/${userId}/sales-role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sales_role: newRole }),
      });
      if (res.ok) toast.success("Sales role updated");
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  return (
    <input
      data-testid={`sales-role-${userId}`}
      className="w-[160px] h-8 px-2 text-xs bg-slate-50 border border-slate-200 rounded focus:ring-1 focus:ring-blue-300 focus:outline-none"
      value={role}
      onChange={(e) => setRole(e.target.value)}
      onBlur={() => { if (role !== currentRole) save(role); }}
      onKeyDown={(e) => { if (e.key === "Enter") save(role); }}
      placeholder="Assign sales role..."
      disabled={saving}
    />
  );
}

function ApproverToggle({ userId, initialValue, isAdmin }) {
  // Per-user "Amplify approver" flag (May 2026). Admins are implicit
  // approvers everywhere — show a locked badge instead of the toggle.
  const [enabled, setEnabled] = useState(!!initialValue);
  const [saving, setSaving] = useState(false);
  if (isAdmin) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" data-testid={`approver-implicit-${userId}`}>
        Auto (admin)
      </Badge>
    );
  }
  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/users/${userId}/can-approve-amplify`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ can_approve_amplify: next }),
      });
      if (!res.ok) {
        setEnabled(!next);
        const b = await res.json().catch(() => ({}));
        toast.error(b.detail || "Failed to update");
      } else {
        toast.success(next ? "Amplify approver granted" : "Amplify approver revoked");
      }
    } catch {
      setEnabled(!next);
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      className={`relative inline-flex h-5 w-9 rounded-full transition ${enabled ? "bg-emerald-500" : "bg-slate-300"} ${saving ? "opacity-60" : ""}`}
      data-testid={`approver-toggle-${userId}`}
      title={enabled ? "Revoke Amplify approval rights" : "Grant Amplify approval rights"}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`}
      />
    </button>
  );
}

function OperationalRoleToggle({ userId, role, label, initialValue, isAdmin, isSelf }) {
  // News Triage Overhaul (Feb 2026): per-user operational role flags driving
  // the Lens (Analyst Queue) and Amplify Kanban routing. Admins get the
  // toggle implicitly via their role-tier, and you can't toggle yourself.
  const [enabled, setEnabled] = useState(!!initialValue);
  const [saving, setSaving] = useState(false);
  if (isSelf) {
    return (
      <Badge className="bg-slate-100 text-slate-500 text-[10px]" data-testid={`oprole-${role}-self-${userId}`}>
        Self
      </Badge>
    );
  }
  if (isAdmin) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" data-testid={`oprole-${role}-implicit-${userId}`}>
        Auto (admin)
      </Badge>
    );
  }
  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/users/${userId}/operational-role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role, value: next }),
      });
      if (!res.ok) {
        setEnabled(!next);
        const b = await res.json().catch(() => ({}));
        toast.error(b.detail || "Failed to update");
      } else {
        toast.success(`${label} ${next ? "granted" : "revoked"}`);
      }
    } catch {
      setEnabled(!next);
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      className={`relative inline-flex h-5 w-9 rounded-full transition ${enabled ? "bg-indigo-500" : "bg-slate-300"} ${saving ? "opacity-60" : ""}`}
      data-testid={`oprole-${role}-toggle-${userId}`}
      title={`${enabled ? "Revoke" : "Grant"} ${label}`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`}
      />
    </button>
  );
}

function FunctionsCell({ u, onEdit }) {
  const chips = summariseUserRoles(u);
  const toneCls = (tone) => ({
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    indigo: "bg-indigo-100 text-indigo-700 border-indigo-200",
    violet: "bg-violet-100 text-violet-700 border-violet-200",
  }[tone] || "bg-slate-100 text-slate-700 border-slate-200");
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`functions-cell-${u.user_id}`}>
      {chips.length === 0 ? (
        <span className="text-xs italic text-slate-400">No functions</span>
      ) : (
        chips.map((c) => (
          <Badge key={c.label} variant="outline" className={`text-[10px] ${toneCls(c.tone)}`}>
            {c.label}
          </Badge>
        ))
      )}
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2 text-xs text-slate-600 hover:text-blue-700"
        onClick={onEdit}
        data-testid={`edit-roles-${u.user_id}`}
      >
        Edit
      </Button>
    </div>
  );
}

export default function UserManagement({ user }) {
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active"); // "active" | "archived" | "all"
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "user" });
  const [inviting, setInviting] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [togglingEmail, setTogglingEmail] = useState(false);
  
  // Password reset state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({ password: "", sendEmail: true });
  const [resettingPassword, setResettingPassword] = useState(false);

  // Roles & Functions popover state (May 2026 — consolidates 4 columns into one).
  const [rolesPopoverOpen, setRolesPopoverOpen] = useState(false);
  const [rolesPopoverUser, setRolesPopoverUser] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, invitesRes, settingsRes] = await Promise.all([
        fetch(`${API}/admin/users`, { credentials: "include" }),
        fetch(`${API}/admin/invitations`, { credentials: "include" }),
        fetch(`${API}/admin/settings`, { credentials: "include" }),
      ]);

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData);
      }

      if (invitesRes.ok) {
        const invitesData = await invitesRes.json();
        setInvitations(invitesData);
      }
      
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setEmailEnabled(settingsData.email_sending_enabled || false);
        setMfaEnabled(settingsData.mfa_enabled !== false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInviteUser = async (e) => {
    e.preventDefault();
    
    if (!inviteForm.email || !inviteForm.name) {
      toast.error("Please fill in all fields");
      return;
    }

    setInviting(true);
    try {
      const response = await fetch(`${API}/admin/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(inviteForm),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Invitation sent to ${inviteForm.email}`);
        setInviteDialogOpen(false);
        setInviteForm({ email: "", name: "", role: "user" });
        fetchData();
        
        // Show the invite URL
        const inviteUrl = `${window.location.origin}/register?token=${data.invite_token}`;
        navigator.clipboard.writeText(inviteUrl);
        toast.info("Invite link copied to clipboard!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (error) {
      console.error("Invite error:", error);
      toast.error("Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleToggleEmail = async () => {
    setTogglingEmail(true);
    try {
      const response = await fetch(`${API}/admin/settings/email-toggle`, {
        method: "PUT",
        credentials: "include",
      });

      if (response.ok) {
        const result = await response.json();
        setEmailEnabled(result.email_sending_enabled);
        toast.success(result.message);
      } else {
        toast.error("Failed to toggle email setting");
      }
    } catch (error) {
      toast.error("Failed to toggle email setting");
    } finally {
      setTogglingEmail(false);
    }
  };

  const handleToggleMfa = async () => {
    try {
      const response = await fetch(`${API}/admin/settings/mfa-toggle`, {
        method: "PUT",
        credentials: "include",
      });
      if (response.ok) {
        const result = await response.json();
        setMfaEnabled(result.mfa_enabled);
        toast.success(result.message);
      } else {
        toast.error("Failed to toggle MFA");
      }
    } catch (error) {
      toast.error("Failed to toggle MFA");
    }
  };

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateTaskCount, setDeactivateTaskCount] = useState(0);
  const [reassignToId, setReassignToId] = useState("");
  const [deactivating, setDeactivating] = useState(false);

  const openDeactivateDialog = async (user) => {
    setDeactivateTarget(user);
    setReassignToId("");
    try {
      const res = await fetch(`${API}/admin/users/${user.user_id}/active-tasks`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDeactivateTaskCount(data.active_task_count || 0);
      } else {
        setDeactivateTaskCount(0);
      }
    } catch {
      setDeactivateTaskCount(0);
    }
  };

  const handleDeactivateUser = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      const response = await fetch(`${API}/admin/users/${deactivateTarget.user_id}/deactivate`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reassign_to_id: reassignToId }),
      });

      if (response.ok) {
        const data = await response.json();
        const msg = data.tasks_reassigned > 0
          ? `User archived. ${data.tasks_reassigned} task(s) reassigned.`
          : "User archived.";
        toast.success(msg);
        setDeactivateTarget(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to archive user");
      }
    } catch (error) {
      toast.error("Failed to archive user");
    } finally {
      setDeactivating(false);
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/activate`, {
        method: "PUT",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("User restored");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to restore user");
      }
    } catch (error) {
      toast.error("Failed to restore user");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        toast.success(`User role updated to ${newRole}`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update role");
      }
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleRevokeInvitation = async (token) => {
    if (!confirm("Revoke this invitation?")) return;

    try {
      const response = await fetch(`${API}/admin/invitations/${token}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Invitation revoked");
        fetchData();
      } else {
        toast.error("Failed to revoke invitation");
      }
    } catch (error) {
      toast.error("Failed to revoke invitation");
    }
  };

  const copyInviteLink = (token) => {
    const inviteUrl = `${window.location.origin}/register?token=${token}`;
    navigator.clipboard.writeText(inviteUrl);
    setCopiedToken(token);
    toast.success("Invite link copied!");
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    
    setResettingPassword(true);
    try {
      const response = await fetch(`${API}/admin/users/${resetPasswordUser.user_id}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          new_password: resetPasswordForm.password || null,
          send_email: resetPasswordForm.sendEmail,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.temp_password && !resetPasswordForm.sendEmail) {
          toast.success(`Password reset! Temporary password: ${result.temp_password}`, { duration: 10000 });
        } else {
          toast.success(`Password reset email sent to ${resetPasswordUser.email}`);
        }
        setResetPasswordDialogOpen(false);
        setResetPasswordForm({ password: "", sendEmail: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reset password");
      }
    } catch (error) {
      toast.error("Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const openResetPasswordDialog = (targetUser) => {
    setResetPasswordUser(targetUser);
    setResetPasswordForm({ password: "", sendEmail: true });
    setResetPasswordDialogOpen(true);
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
          <p className="text-slate-600 mt-2">Only administrators can manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            User Management
          </h1>
          <p className="text-slate-600 mt-1">
            Add, remove, and manage team members
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={() => setInviteDialogOpen(true)} data-testid="invite-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        </div>
      </div>

      {/* Email Sending Toggle */}
      <div className={`p-4 rounded-xl border ${emailEnabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {emailEnabled ? (
              <MailCheck className="w-6 h-6 text-green-600" />
            ) : (
              <MailX className="w-6 h-6 text-amber-600" />
            )}
            <div>
              <h3 className="font-semibold text-slate-900">
                Email Sending: {emailEnabled ? 'ENABLED' : 'DISABLED'}
              </h3>
              <p className="text-sm text-slate-600">
                {emailEnabled 
                  ? 'Emails WILL be sent to real clients when you use campaigns or send emails.' 
                  : 'Emails will NOT be sent. Safe for testing with real client data.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">{emailEnabled ? 'On' : 'Off'}</span>
            <Switch
              checked={emailEnabled}
              onCheckedChange={handleToggleEmail}
              disabled={togglingEmail}
              data-testid="email-toggle"
            />
          </div>
        </div>
      </div>

      {/* MFA Toggle */}
      <div className={`p-4 rounded-xl border ${mfaEnabled ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="mfa-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className={`w-6 h-6 ${mfaEnabled ? 'text-green-600' : 'text-red-600'}`} />
            <div>
              <h3 className="font-semibold text-slate-900">
                MFA: {mfaEnabled ? 'ENABLED' : 'DISABLED'}
              </h3>
              <p className="text-sm text-slate-600">
                {mfaEnabled 
                  ? 'Two-factor authentication is active. Users must verify with email code.' 
                  : 'MFA is OFF. Users can log in with password only. Re-enable after maintenance.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">{mfaEnabled ? 'On' : 'Off'}</span>
            <Switch
              checked={mfaEnabled}
              onCheckedChange={handleToggleMfa}
              data-testid="mfa-toggle"
            />
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold text-slate-900">
            {statusFilter === "active" ? "Active" : statusFilter === "archived" ? "Archived" : "All"} Users
            {" "}({users.filter((u) =>
              statusFilter === "active" ? u.is_active :
              statusFilter === "archived" ? !u.is_active : true
            ).length})
          </h2>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="user-status-filter">
            {[
              { id: "active", label: "Active" },
              { id: "archived", label: "Archived" },
              { id: "all", label: "All" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setStatusFilter(opt.id)}
                data-testid={`user-filter-${opt.id}`}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  statusFilter === opt.id
                    ? "bg-white shadow-sm text-slate-900 font-medium"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {opt.label}
                {opt.id === "archived" && users.filter((u) => !u.is_active).length > 0 && (
                  <span className="ml-1 text-[10px] text-slate-500">
                    ({users.filter((u) => !u.is_active).length})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Functions</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.filter((u) =>
              statusFilter === "active" ? u.is_active :
              statusFilter === "archived" ? !u.is_active : true
            ).map((u) => (
              <TableRow key={u.user_id} className={!u.is_active ? "bg-slate-50/60" : ""}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  {u.user_id === user.user_id ? (
                    <Badge className={ROLE_INFO[u.role]?.color || "bg-slate-100"}>
                      {u.role === "admin" && <Shield className="w-3 h-3 mr-1" />}
                      {u.role === "viewer" && <Eye className="w-3 h-3 mr-1" />}
                      {u.role === "user" && <UserCog className="w-3 h-3 mr-1" />}
                      {ROLE_INFO[u.role]?.label || u.role}
                    </Badge>
                  ) : (
                    <Select
                      value={u.role}
                      onValueChange={(value) => handleChangeRole(u.user_id, value)}
                    >
                      <SelectTrigger className="w-[120px] h-8">
                        <Badge className={ROLE_INFO[u.role]?.color || "bg-slate-100"}>
                          {ROLE_INFO[u.role]?.label || u.role}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-red-600" />
                            <div>
                              <div className="font-medium">Admin</div>
                              <div className="text-xs text-slate-500">Full access</div>
                            </div>
                          </div>
                        </SelectItem>
                        <SelectItem value="user">
                          <div className="flex items-center gap-2">
                            <UserCog className="w-4 h-4 text-blue-600" />
                            <div>
                              <div className="font-medium">User</div>
                              <div className="text-xs text-slate-500">Can edit data</div>
                            </div>
                          </div>
                        </SelectItem>
                        <SelectItem value="viewer">
                          <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4 text-slate-600" />
                            <div>
                              <div className="font-medium">Viewer</div>
                              <div className="text-xs text-slate-500">Read-only</div>
                            </div>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  {/* Consolidated "Functions" cell (May 2026 — replaces 4 columns) */}
                  <FunctionsCell
                    u={u}
                    onEdit={() => {
                      setRolesPopoverUser(u);
                      setRolesPopoverOpen(true);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Badge variant={u.is_active ? "outline" : "secondary"} className={!u.is_active ? "bg-slate-100 text-slate-600 border-slate-300" : ""}>
                    {u.is_active ? "Active" : "Archived"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {u.user_id !== user.user_id && (
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openResetPasswordDialog(u)}
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {u.is_active ? (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => openDeactivateDialog(u)}
                          data-testid={`archive-${u.email}`}
                          title="Archive User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleActivateUser(u.user_id)}
                          data-testid={`restore-${u.email}`}
                          title="Restore User"
                        >
                          Restore
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">
              Pending Invitations ({invitations.length})
            </h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((inv) => (
                <TableRow key={inv.token}>
                  <TableCell className="font-medium">{inv.name}</TableCell>
                  <TableCell>{inv.email}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{inv.role}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyInviteLink(inv.token)}
                      >
                        {copiedToken === inv.token ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRevokeInvitation(inv.token)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInviteUser} className="space-y-4">
            <div>
              <Label htmlFor="invite-name">Name</Label>
              <Input
                id="invite-name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="John Doe"
                className="mt-1"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="john@resiligent.com"
                className="mt-1"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-slate-600" />
                      <div>
                        <div className="font-medium">Viewer</div>
                        <div className="text-xs text-slate-500">Read-only access</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="user">
                    <div className="flex items-center gap-2">
                      <UserCog className="w-4 h-4 text-blue-600" />
                      <div>
                        <div className="font-medium">User</div>
                        <div className="text-xs text-slate-500">Can create/edit data</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-red-600" />
                      <div>
                        <div className="font-medium">Admin</div>
                        <div className="text-xs text-slate-500">Full access</div>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                {ROLE_INFO[inviteForm.role]?.description}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={inviting} data-testid="send-invite-btn">
                {inviting ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Reset User Password
            </DialogTitle>
            <DialogDescription>
              Reset password for {resetPasswordUser?.name} ({resetPasswordUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-password">New Password (optional)</Label>
              <Input
                id="new-password"
                type="text"
                value={resetPasswordForm.password}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, password: e.target.value })}
                placeholder="Leave blank to generate random password"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave blank to generate a secure temporary password
              </p>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="send-email"
                checked={resetPasswordForm.sendEmail}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, sendEmail: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300"
              />
              <label htmlFor="send-email" className="text-sm">
                <span className="font-medium">Send password via email</span>
                <p className="text-slate-500 text-xs">
                  User will receive an email with their new password
                </p>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword}
              disabled={resettingPassword}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {resettingPassword ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate User Dialog with Task Reassignment */}
      <Dialog open={!!deactivateTarget} onOpenChange={(open) => { if (!open) setDeactivateTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Archive User</DialogTitle>
            <DialogDescription>
              Archiving <strong>{deactivateTarget?.name}</strong> ({deactivateTarget?.email}). They won't be able to log in. Their history is preserved and they can be restored at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {deactivateTaskCount > 0 ? (
              <>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800">
                    This user has <strong>{deactivateTaskCount}</strong> active task{deactivateTaskCount !== 1 ? "s" : ""}.
                  </p>
                  <p className="text-xs text-amber-600 mt-1">Select a team member to reassign tasks to, or proceed without reassignment.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Reassign tasks to</Label>
                  <Select value={reassignToId || "none"} onValueChange={(v) => setReassignToId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="reassign-user-select">
                      <SelectValue placeholder="Select a team member" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Don't reassign (tasks will remain)</SelectItem>
                      {users
                        .filter(u => u.is_active && u.user_id !== deactivateTarget?.user_id)
                        .map(u => (
                          <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500">This user has no active tasks. They can be safely archived.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)} disabled={deactivating}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeactivateUser}
              disabled={deactivating}
              data-testid="confirm-archive-btn"
            >
              {deactivating ? "Archiving..." : "Archive User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Roles & Functions popover — consolidates 4 columns into one (May 2026). */}
      <UserRolesPopover
        open={rolesPopoverOpen}
        onClose={() => setRolesPopoverOpen(false)}
        user={rolesPopoverUser}
        isSelf={rolesPopoverUser?.user_id === user?.user_id}
        onSaved={fetchData}
      />
    </div>
  );
}
